import React, { useCallback, useEffect, useState} from 'react';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import PickScore from './PickScore';
import {useData} from '../../../contexts/DataContext';
import { normalizePicks, applyPickChange } from '../../../lib/normalizePicks';
import { isTiedScorePick } from '../../../lib/scoreValidation';
import {Button} from '@mui/material';
import Pagination from '@mui/material/Pagination';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import LinearProgress from '@mui/material/LinearProgress';
import Box from '@mui/material/Box';

const Alert = React.forwardRef(function Alert(
    props,
    ref,
) {
    return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const styles = {
    submit: {
        backgroundColor: '#1976d2',
        display: 'flex',
        margin: 'auto',
        marginTop: '30px',
        width: '50%',
    },
    container: {
        display: 'flex',
        flexDirection: 'column',
        margin: 'auto',
        minWidth: '1055px',
        width: '50%',
        marginTop: '128px'
    },
    paper: {
        margin: 'auto',
        width: '70%',
        padding: '20px',
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'center',
        textAlign: 'center'
    },
    header: {
        margin: 'auto',
        width: '70%',
        padding: '20px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center'
    }
  };

const MyPicks = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const { getGamesForWeek,
            savePicks,
            getPicksForUser,
            getCurrentWeek,
            currentSeason,
        } = useData();
    const [error, setError] = useState('');
    const [openError, setOpenError] = useState();
    const [message , setMessage] = useState('');
    const [openMessage , setOpenMessage] = useState();
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [page, setPage] = React.useState(1);
    const [numberOfWeeks, setNumberOfWeeks] = React.useState(1);
    const [userPicks, setUserPicks] = useState([]);
    // One stable callback for every card. It merges a card's change into a
    // NEW pick object for that game only, so each memoized PickScore
    // re-renders only when its own pick changes — not on every keystroke in
    // another card.
    const handlePickChange = useCallback((gameId, changes) => {
        setUserPicks((prev) => applyPickChange(prev, gameId, changes));
    }, []);
    const [games, setGames] = useState([]);

    const season = currentSeason;
    const [fetchError, setFetchError] = useState('');

    useEffect(() => {
        if (!season) return; // Wait for real season to load
        let cancelled = false;
        const fetchData = async () => {
            setIsLoading(true);
            setFetchError('');
            try {
                const currentWeek = await getCurrentWeek(season);
                if (cancelled) return;
                setPage(currentWeek);
                setNumberOfWeeks(currentWeek);

                const [games, picks] = await Promise.all([
                    getGamesForWeek(season, currentWeek),
                    getPicksForUser(season, currentWeek)
                ]);
                if (cancelled) return;

                setGames(games);
                setDisabledDefaults(picks, games);
            } catch (error) {
                console.error('Error loading picks:', error);
                if (!cancelled) setFetchError('Failed to load games. Please try refreshing.');
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        };

        fetchData();
        return () => { cancelled = true; };
    }, [season, getCurrentWeek, getGamesForWeek, getPicksForUser]);

    const setDisabledDefaults = (userPicks, games) => {
        const tempPicks = { ...normalizePicks(userPicks, games) };

        for (const game of games) {
            if (game.disabled && !tempPicks[game.id]) {
                tempPicks[game.id] = game.isPickScore
                    ? { awayScore: 0, homeScore: 0 }
                    : { awayWin: false, homeWin: false };
            }
        }

        setUserPicks(tempPicks);
    }

    const turnPageRef = React.useRef(0);
    const turnPage = async (event, value) => {
        const requestId = ++turnPageRef.current;
        setIsLoading(true);
        setPage(value);

        try {
            const [games, picks] = await Promise.all([
                getGamesForWeek(season, value),
                getPicksForUser(season, value)
            ]);
            // Only update if this is still the latest request
            if (requestId !== turnPageRef.current) return;
            setGames(games);
            setDisabledDefaults(picks, games);
        } catch (error) {
            console.error('Error loading week:', error);
            if (requestId === turnPageRef.current) {
                setFetchError('Failed to load week. Please try again.');
            }
        } finally {
            if (requestId === turnPageRef.current) setIsLoading(false);
        }
    };

    const submitPicks = async () => {
        // A tied score names no winner and can't score; make the player fix it
        // before anything is saved. Games that have kicked off are locked (and
        // default to 0-0 when never picked), so they're not checked.
        const tied = games.filter((game) =>
            game.isPickScore && !game.disabled && isTiedScorePick(userPicks[game.id]));
        if (tied.length > 0) {
            const names = tied.map((game) => `${game.awayTeam?.name} at ${game.homeTeam?.name}`).join(', ');
            setError(`Picks not saved. A score prediction can't be a tie: ${names}.`);
            setOpenError(true);
            return;
        }

        setIsSaving(true);
        try {
            await savePicks(userPicks, season, page);
            setMessage('Your picks have been saved.');
            setOpenMessage(true);
        } catch (error) {
            setError(error.message);
            setOpenError(true);
        } finally {
            setIsSaving(false);
        }
    }

    const handleClose = () => {
        setOpenError(false);
        setOpenMessage(false);
    }
    
    return (
        <div style={{
            ...styles.container,
            ...(isMobile && { minWidth: 'unset', width: '100%', marginTop: '80px', padding: '0 8px', boxSizing: 'border-box', overflowX: 'hidden' }),
        }}>
            <div style={{
                ...styles.paper,
                ...(isMobile && { width: '100%', padding: '10px', fontSize: '14px' }),
            }}> Each week you will pick the winners for a set of games.  Some of the games will require you to enter a score. </div>
            <div style={styles.header}>
                <div style={{
                    ...styles.paper,
                    ...(isMobile && { width: '100%', padding: '10px' }),
                }}>
                    <span> Week: </span>
                    {isMobile ? (
                        <FormControl size="small" sx={{ minWidth: 80 }}>
                            <Select
                                value={page}
                                onChange={(e) => turnPage(e, e.target.value)}
                            >
                                {Array.from({ length: numberOfWeeks }, (_, i) => (
                                    <MenuItem key={i + 1} value={i + 1}>{i + 1}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    ) : (
                        <Pagination
                            count={numberOfWeeks}
                            shape="rounded"
                            page={page}
                            onChange={turnPage}
                        />
                    )}
                </div>
                {
                    isLoading && <Box sx={{ width: '100%' }}>
                    <LinearProgress />
                  </Box>
                }
                {fetchError && (
                    <Box sx={{ color: 'error.main', textAlign: 'center', mt: 2 }}>
                        {fetchError}
                    </Box>
                )}
            </div>
            {
                games && games.map((game, index) => {
                    return <PickScore {... game}
                        key={game.id || `${game.awayTeam?.name || index}@${game.homeTeam?.name || index}`}
                        gameId={game.id}
                        pick={userPicks[game.id]}
                        onPickChange={handlePickChange} />
                })
            }
            
            <Button
                type="submit"
                fullWidth
                variant="contained"
                color="secondary"
                sx={{ ...styles.submit, ...(isMobile && { width: '90%' }) }}
                onClick={submitPicks}
                loading={isSaving}
            >
            Save Picks
          </Button>
          { error && 
            <Snackbar
                open={openError}
                autoHideDuration={6000}
                onClose={handleClose}
            >
                <Alert onClose={handleClose} severity="error" sx={{ width: '100%' }}>{error}</Alert>
            </Snackbar>
          }
          {
            message && 
            <Snackbar
                open={openMessage}
                autoHideDuration={6000}
                onClose={handleClose}
            >
                <Alert onClose={handleClose} severity="success" sx={{ width: '100%' }}>{message}</Alert>
            </Snackbar>
          }
          
        </div>
    )
}

export default MyPicks;
